import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ProfileHeader from '../components/ProfileHeader';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { useSelectedOrganizationContext } from '../utils/organizationSelection';
import { getOrganizationBookings, getOrganizationBookingRequests } from '../utils/booking';
import { getOrganizationBookingPayments } from '../utils/bookingPayments';
import { getOrganizationClientProfiles, getOrganizationLessons } from '../utils/clientCRM';
import {
  getConversionByInstructor,
  getConversionByService,
  getFollowUpConversionRate,
  getFullConversionFunnel,
  getPackageOfferConversionRate,
  getPublicServiceToBookingConversion,
  getOrganizationRetentionActions,
  getRetentionRevenueSummary,
} from '../utils/retentionActions';
import {
  getOrganizationTemplates,
  getTemplateUsageSummary,
  getRetentionMessageTemplates,
} from '../utils/retentionMessaging';
import { getOrganizationCustomerMemberships } from '../utils/servicePackages';
import { getOrganizationServices } from '../utils/services';
import { canViewRetentionReportingPage } from '../utils/platformAccess';

const REPORT_TABS = {
  summary: 'Summary',
  instructors: 'Instructors',
  services: 'Services',
  templates: 'Templates',
};

function formatPercent(value) {
  return `${Math.round((Number(value) || 0) * 100)}%`;
}

function RetentionReportingPage() {
  const { reportView = 'summary' } = useParams() || {};
  const { currentUser, userData } = useAuth() || {};
  const { selectedOrganizationId, selectedOrganization } = useSelectedOrganizationContext(userData) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState({
    bookings: [],
    bookingRequests: [],
    memberships: [],
    services: [],
    lessons: [],
    retentionActions: [],
    bookingPayments: [],
    templates: [],
    organizationTemplates: [],
    clientProfiles: [],
  });

  const organizationId = selectedOrganizationId || userData?.orgId || '';
  const canViewReporting = canViewRetentionReportingPage(userData || {}, organizationId);

  const loadReporting = useCallback(async () => {
    if (!organizationId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [
        clientProfiles,
        bookings,
        bookingRequests,
        memberships,
        services,
        retentionActions,
        bookingPayments,
        templates,
        organizationTemplates,
        lessons,
      ] = await Promise.all([
        getOrganizationClientProfiles(db, organizationId),
        getOrganizationBookings(db, organizationId, { startAfter: (() => { const d = new Date(); d.setDate(d.getDate() - 90); return d; })(), endBefore: (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d; })() }),
        getOrganizationBookingRequests(db, organizationId),
        getOrganizationCustomerMemberships(db, organizationId),
        getOrganizationServices(db, organizationId),
        getOrganizationRetentionActions(db, organizationId),
        getOrganizationBookingPayments(db, organizationId),
        getRetentionMessageTemplates(db, { organizationId, includeInactive: true }),
        getOrganizationTemplates(db, organizationId, { includeInactive: true }),
        // §44/§46 — org-wide delivered-lesson evidence (delivery-truth), incl. no-Auth clients; not an N+1 per-profile scan.
        getOrganizationLessons(db, organizationId),
      ]);

      setReportData({
        bookings: bookings || [],
        bookingRequests: bookingRequests || [],
        memberships: memberships || [],
        services: services || [],
        lessons: lessons || [],
        retentionActions: retentionActions || [],
        bookingPayments: bookingPayments || [],
        templates: templates || [],
        organizationTemplates: organizationTemplates || [],
        clientProfiles: clientProfiles || [],
      });
    } catch (loadError) {
      setError(loadError?.message || 'Could not load retention reporting right now.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, organizationId]);

  useEffect(() => {
    loadReporting();
  }, [loadReporting]);

  const {
    bookings,
    bookingRequests,
    memberships,
    services,
    lessons,
    retentionActions,
    bookingPayments,
    templates,
    organizationTemplates,
  } = reportData;

  const fullFunnel = useMemo(() => getFullConversionFunnel({
    bookingRequests,
    bookings,
    lessons,
    actions: retentionActions,
    memberships,
  }) || {
    publicToBooking: { conversionRate: 0 },
    bookingToLesson: { conversionRate: 0 },
    lessonToRebook: { conversionRate: 0 },
    followUpToRebook: { conversionRate: 0 },
    packageOfferToPurchase: { conversionRate: 0 },
  }, [bookingRequests, bookings, lessons, memberships, retentionActions]);

  const followUpSummary = useMemo(
    () => getFollowUpConversionRate(retentionActions, bookings) || { conversionRate: 0 },
    [bookings, retentionActions]
  );
  const packageOfferSummary = useMemo(
    () => getPackageOfferConversionRate(retentionActions, memberships) || { conversionRate: 0 },
    [memberships, retentionActions]
  );
  const retentionRevenue = useMemo(
    () => getRetentionRevenueSummary(retentionActions, { bookings, bookingPayments }) || { attributableRevenue: 0 },
    [bookingPayments, bookings, retentionActions]
  );
  const serviceBreakdown = useMemo(
    () => getConversionByService(services, { bookingRequests, bookings, lessons, actions: retentionActions, memberships }) || [],
    [bookingRequests, bookings, lessons, memberships, retentionActions, services]
  );
  const instructorIds = useMemo(
    () => Array.from(new Set((bookings || []).map((booking) => booking.instructorId).filter(Boolean))),
    [bookings]
  );
  const instructorBreakdown = useMemo(
    () => instructorIds.map((instructorId) => getConversionByInstructor(instructorId, {
      bookingRequests,
      bookings,
      lessons,
      actions: retentionActions,
      memberships,
    })) || [],
    [bookingRequests, bookings, instructorIds, lessons, memberships, retentionActions]
  );
  const publicServiceConversion = useMemo(
    () => getPublicServiceToBookingConversion(services, bookingRequests, bookings) || [],
    [bookingRequests, bookings, services]
  );
  const templateUsageSummary = useMemo(
    () => getTemplateUsageSummary(retentionActions, templates, { bookings, memberships }) || [],
    [bookings, memberships, retentionActions, templates]
  );

  if (!canViewReporting) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <ProfileHeader />
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-950">Retention Reporting</h1>
          <p className="mt-3 text-sm text-slate-600">You do not have access to retention reporting for this organization.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <ProfileHeader />

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Retention Reporting</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">
              {selectedOrganization?.organizationName || userData?.orgName || 'Organization'} Reporting
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Practical retention performance reporting across follow-ups, packages, templates, instructors, and services.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/retention" className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200">
              Back to Workspace
            </Link>
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(REPORT_TABS).map(([key, label]) => (
            <Link
              key={key}
              to={key === 'summary' ? '/retention/reporting' : `/retention/reporting/${key}`}
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                key === reportView
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {label}
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Follow-Up Conversion</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{formatPercent(followUpSummary.conversionRate)}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Package Offer Conversion</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{formatPercent(packageOfferSummary.conversionRate)}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Retention Revenue</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{retentionRevenue.attributableRevenue || 0}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Template Usage</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{templateUsageSummary.reduce((total, item) => total + item.usageCount, 0)}</p>
        </article>
      </section>

      {loading ? (
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-600">Loading reporting...</p>
        </section>
      ) : (
        <>
          {(reportView === 'summary' || !REPORT_TABS[reportView]) && (
            <section className="mt-6 grid gap-6 lg:grid-cols-2">
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Full Funnel</h2>
                <div className="mt-4 space-y-2 text-sm text-slate-600">
                  <p>Public profile to booking: {formatPercent(fullFunnel.publicToBooking?.conversionRate)}</p>
                  <p>Booking to lesson: {formatPercent(fullFunnel.bookingToLesson?.conversionRate)}</p>
                  <p>Lesson to rebook: {formatPercent(fullFunnel.lessonToRebook?.conversionRate)}</p>
                  <p>Follow-up to rebook: {formatPercent(fullFunnel.followUpToRebook?.conversionRate)}</p>
                  <p>Package offer to purchase: {formatPercent(fullFunnel.packageOfferToPurchase?.conversionRate)}</p>
                </div>
              </article>
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Template Performance Snapshot</h2>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  {templateUsageSummary.length === 0 ? (
                    <p>No template usage has been logged yet.</p>
                  ) : templateUsageSummary.slice(0, 4).map((template) => (
                    <div key={template.templateKey} className="rounded-2xl bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">{template.templateName}</p>
                      <p>Usage count: {template.usageCount}</p>
                      <p>Follow-ups: {template.followUpSentCount} | Package offers: {template.packageOfferCount}</p>
                    </div>
                  ))}
                </div>
              </article>
            </section>
          )}

          {reportView === 'instructors' && (
            <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Instructor Breakdown</h2>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                {instructorBreakdown.length === 0 ? (
                  <p>No instructor attribution data is available yet.</p>
                ) : instructorBreakdown.map((instructor) => (
                  <div key={instructor.instructorId || 'unassigned'} className="rounded-2xl bg-slate-50 p-4">
                    <p className="font-semibold text-slate-900">{instructor.instructorId || 'Unassigned'}</p>
                    <p>Public to booking: {formatPercent(instructor.conversionRate)}</p>
                    <p>Lesson to package: {formatPercent(instructor.packagePurchaseCount && instructor.completedLessonClientCount ? instructor.packagePurchaseCount / instructor.completedLessonClientCount : 0)}</p>
                    <p>Follow-up to rebook: {formatPercent(instructor.rebookedAfterFollowUpCount && instructor.followUpCount ? instructor.rebookedAfterFollowUpCount / instructor.followUpCount : 0)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {reportView === 'services' && (
            <section className="mt-6 grid gap-6 lg:grid-cols-2">
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Service Breakdown</h2>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  {serviceBreakdown.length === 0 ? (
                    <p>No service attribution data is available yet.</p>
                  ) : serviceBreakdown.map((service) => (
                    <div key={service.serviceId || service.serviceName} className="rounded-2xl bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">{service.serviceName}</p>
                      <p>Public to booking: {formatPercent(service.conversionRate)}</p>
                      <p>Booking to lesson: {formatPercent(service.lessonLinkedCount && service.bookingCount ? service.lessonLinkedCount / service.bookingCount : 0)}</p>
                      <p>Lesson to package: {formatPercent(service.packagePurchaseCount && service.completedLessonClientCount ? service.packagePurchaseCount / service.completedLessonClientCount : 0)}</p>
                    </div>
                  ))}
                </div>
              </article>
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Public Service Selection</h2>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  {publicServiceConversion.length === 0 ? (
                    <p>No public service selection data is available yet.</p>
                  ) : publicServiceConversion.map((service) => (
                    <div key={service.serviceId || service.serviceName} className="rounded-2xl bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">{service.serviceName}</p>
                      <p>Public requests: {service.publicRequestCount}</p>
                      <p>Confirmed from public: {service.publicConfirmedCount}</p>
                      <p>Conversion: {formatPercent(service.conversionRate)}</p>
                    </div>
                  ))}
                </div>
              </article>
            </section>
          )}

          {reportView === 'templates' && (
            <section className="mt-6 grid gap-6 lg:grid-cols-2">
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Template Usage</h2>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  {templateUsageSummary.length === 0 ? (
                    <p>No template usage has been logged yet.</p>
                  ) : templateUsageSummary.map((template) => (
                    <div key={template.templateKey} className="rounded-2xl bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">{template.templateName}</p>
                      <p>Usage count: {template.usageCount}</p>
                      <p>Rebook conversions: {template.rebookConversionCount}</p>
                      <p>Package conversions: {template.packageConversionCount}</p>
                    </div>
                  ))}
                </div>
              </article>
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Template Catalog</h2>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  {(templates || []).map((template) => (
                    <div key={template.templateKey || template.templateId} className="rounded-2xl bg-slate-50 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-900">{template.name}</p>
                        <span className="rounded-full bg-white px-2 py-1 text-xs text-slate-600">
                          {template.isBuiltIn ? 'Built-in' : 'Organization'}
                        </span>
                        {template.isDefault && (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs text-emerald-700">Default</span>
                        )}
                        {!template.active && (
                          <span className="rounded-full bg-slate-200 px-2 py-1 text-xs text-slate-700">Archived</span>
                        )}
                      </div>
                      <p className="mt-2 line-clamp-3">{template.body}</p>
                    </div>
                  ))}
                  <p className="text-xs text-slate-500">
                    {organizationTemplates.length} organization template{organizationTemplates.length === 1 ? '' : 's'} saved for this org.
                  </p>
                </div>
              </article>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default RetentionReportingPage;
